import React from "react";
import { SaveOutlined, LinkOutlined, DeleteOutlined, DownOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import { RFI_TAG_WIDTH, RFI_TAG_HEIGHT, CONNECTION_INNER_RECT_INSET } from "../constants";

const ConnectionBox = ({
  connectionBoxStart,
  connectionBoxEnd,
  zoom,
  canvasRef,
  onMoveStart,
  onResizeStart,
  onPublish,
  onLink,
  onDelete,
  linkedRfi,
  linkedDrawing,
  linkedPhotoSrc,
  onTagClick,
  onTagClose,
  onPhotoTagClick,
  onPhotoUnlink,
  canDelete = true,
}) => {
  const getMousePos = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    let clientX = e.clientX;
    let clientY = e.clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }
    return {
      x: (clientX - rect.left) / zoom,
      y: (clientY - rect.top) / zoom,
    };
  };

  if (!connectionBoxStart || !connectionBoxEnd) return null;

  const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
  const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
  const boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
  const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);

  const tagWidth = RFI_TAG_WIDTH;
  const tagHeight = RFI_TAG_HEIGHT;
  const innerRectInset = CONNECTION_INNER_RECT_INSET;
  const tagX = boxX + boxWidth - innerRectInset;
  const tagY = boxY + boxHeight - innerRectInset - tagHeight;
  const safeZoom = Math.max(zoom || 1, 0.1);
  const toolbarScale = Math.min(Math.max(1 / safeZoom, 1), 2.4);
  const toolbarHeight = 25 * toolbarScale;
  const toolbarGap = 4 * toolbarScale;
  const toolbarPaddingY = 2 * toolbarScale;
  const toolbarPaddingX = 4 * toolbarScale;
  const toolbarFontSize = 11 * toolbarScale;
  const toolbarMinWidth = 150 * toolbarScale;

  const corners = [
    { x: boxX, y: boxY, handle: 'nw' },
    { x: boxX + boxWidth, y: boxY, handle: 'ne' },
    { x: boxX, y: boxY + boxHeight, handle: 'sw' },
    { x: boxX + boxWidth, y: boxY + boxHeight, handle: 'se' },
  ];

  return (
    <g>
      {/* Outer dashed rectangle */}
      <rect
        x={boxX}
        y={boxY}
        width={boxWidth}
        height={boxHeight}
        fill="rgba(24, 144, 255, 0.1)"
        stroke="#1890ff"
        strokeWidth={2}
        strokeDasharray="5,5"
        style={{ cursor: "move", pointerEvents: "all" }}
        onMouseDown={(e) => {
          e.stopPropagation();
          const pt = getMousePos(e);
          onMoveStart(pt);
        }}
      />
      <rect
        x={boxX + innerRectInset}
        y={boxY + innerRectInset}
        width={boxWidth - 2 * innerRectInset}
        height={boxHeight - 2 * innerRectInset}
        fill="none"
        stroke="#ff4d4f"
        strokeWidth={1.5}
        style={{ cursor: "move", pointerEvents: "none" }}
      />
      {/* Resize handles */}
      {corners.map((corner, idx) => (
        <rect
          key={idx}
          x={corner.x - 4}
          y={corner.y - 4}
          width={8}
          height={8}
          fill="#1890ff"
          stroke="#fff"
          strokeWidth={1}
          style={{
            cursor: `${
              corner.handle === "nw"
                ? "nw"
                : corner.handle === "ne"
                  ? "ne"
                  : corner.handle === "sw"
                    ? "sw"
                    : "se"
            }-resize`,
            pointerEvents: "all",
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            onResizeStart(corner.handle);
          }}
        />
      ))}
      {/* Toolbar - positioned above the connection box */}
      <foreignObject
        x={boxX}
        y={boxY - toolbarHeight - 5 * toolbarScale}
        width={Math.max(boxWidth, toolbarMinWidth)}
        height={toolbarHeight}
      >
        <div
          style={{
            display: "flex",
            gap: toolbarGap,
            padding: `${toolbarPaddingY}px ${toolbarPaddingX}px`,
            background: "#4A5568",
            alignItems: "center",
            height: "100%",
            cursor: "move",
          }}
          onMouseDown={(e) => {
            // Allow dragging from toolbar, but not from buttons
            if (e.target.tagName === "BUTTON" || e.target.closest("button")) {
              return;
            }
            e.stopPropagation();
            e.preventDefault();
            const pt = getMousePos(e);
            onMoveStart(pt);
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPublish();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: "transparent",
              border: "none",
              color: "white",
              cursor: "pointer",
              fontSize: toolbarFontSize,
              display: "flex",
              alignItems: "center",
              gap: toolbarGap,
            }}
          >
            <SaveOutlined />
            Save
          </button>
          <Dropdown
            menu={{
              items: [
                {
                  key: "rfi",
                  label: "RFI",
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    onLink("rfi");
                  },
                },
                {
                  key: "submittal",
                  label: "Submittal",
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    onLink("submittal");
                  },
                },
                {
                  key: "punchlist",
                  label: "Punch List",
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    onLink("punchlist");
                  },
                },
                {
                  key: "drawing",
                  label: "Drawing",
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    onLink("drawing");
                  },
                },
                {
                  key: "photo",
                  label: "Photo",
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    onLink("photo");
                  },
                },
              ],
            }}
            trigger={["click"]}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                background: "transparent",
                border: "none",
                color: "white",
                cursor: "pointer",
                fontSize: toolbarFontSize,
                display: "flex",
                alignItems: "center",
                gap: toolbarGap,
              }}
            >
              <LinkOutlined /> Link{" "}
              <DownOutlined style={{ fontSize: 8 * toolbarScale }} />
            </button>
          </Dropdown>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!canDelete) return;
              onDelete();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={!canDelete}
            title={
              canDelete ? "Delete" : "Consultants cannot delete saved annotations"
            }
            style={{
              background: "transparent",
              border: "none",
              color: "white",
              cursor: canDelete ? "pointer" : "not-allowed",
              opacity: canDelete ? 1 : 0.4,
              fontSize: toolbarFontSize,
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: toolbarGap,
            }}
          >
            <DeleteOutlined />
          </button>
        </div>
      </foreignObject>
      <g>
        <rect
          x={tagX}
          y={tagY}
          width={tagWidth}
          height={tagHeight}
          fill={
            linkedPhotoSrc || linkedRfi || linkedDrawing ? "#ff4d4f" : "#e8e8e8"
          }
          stroke={linkedPhotoSrc || linkedRfi || linkedDrawing ? "none" : "#d9d9d9"}
          strokeWidth={1}
          rx={4}
          style={{
            cursor:
              linkedPhotoSrc || linkedRfi || linkedDrawing
                ? "pointer"
                : "default",
            pointerEvents: "all",
          }}
          onClick={(e) => {
            if (linkedPhotoSrc && onPhotoTagClick) {
              e.stopPropagation();
              onPhotoTagClick(linkedPhotoSrc);
            } else if (linkedDrawing && onTagClick) {
              e.stopPropagation();
              onTagClick({ ...linkedDrawing, type: "drawing" });
            } else if (linkedRfi && onTagClick) {
              e.stopPropagation();
              onTagClick(linkedRfi);
            }
          }}
        />
        <text
          x={tagX + 6}
          y={tagY + tagHeight / 2}
          fill={
            linkedPhotoSrc || linkedRfi || linkedDrawing ? "white" : "#999"
          }
          fontSize={11}
          fontWeight="bold"
          textAnchor="start"
          dominantBaseline="middle"
          style={{ pointerEvents: "none", userSelect: "none" }}
        >
          {linkedPhotoSrc
            ? "Photo"
            : linkedDrawing
              ? linkedDrawing.targetDrawingNumber || "Sheet"
              : linkedRfi
                ? linkedRfi.type === "submittal"
                  ? linkedRfi.rfiNumber
                  : linkedRfi.type === "punchlist"
                    ? `PL ${linkedRfi.rfiNumber}`
                    : `RFI ${linkedRfi.rfiNumber}`
                : "No Link"}
        </text>
        {(linkedPhotoSrc || linkedRfi || linkedDrawing) &&
          (onTagClose || onPhotoUnlink) && (
          <>
            <circle
              cx={tagX + tagWidth + 6}
              cy={tagY}
              r={6}
              fill="#ff4d4f"
              style={{ cursor: "pointer", pointerEvents: "all" }}
              onClick={(e) => {
                e.stopPropagation();
                if (linkedPhotoSrc && onPhotoUnlink) {
                  onPhotoUnlink();
                } else if (linkedDrawing && onTagClose) {
                  onTagClose({ ...linkedDrawing, type: "drawing" });
                } else if (linkedRfi && onTagClose) {
                  onTagClose(linkedRfi);
                }
              }}
            />
            <text
              x={tagX + tagWidth + 6}
              y={tagY}
              fill="white"
              fontSize={10}
              fontWeight="bold"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ pointerEvents: "none", userSelect: "none" }}
            >
              ×
            </text>
          </>
        )}
      </g>
    </g>
  );
};

export default ConnectionBox;

